import React, { useEffect, useState } from 'react'
import Grid from '@mui/material/Grid'
import Typography from '@mui/material/Typography'
import Divider from '@/v2/components/common/Divider'
import Button from '@/v2/components/common/Button'
import { Wrapper } from './styles'
import { useSyncPatientData } from '@/v2/hooks/insurance/useEligibilities'
import { useSnackbarContext } from '@/v2/contexts/SnackbarContext'
import { format, parseISO } from 'date-fns'

const formatGender = gender => {
  if (!gender) return ''
  return gender.charAt(0).toUpperCase() + gender.slice(1)
}

const PatientData = ({
  patient,
  existentPatientData,
  insuranceId,
  eligibilityId,
}) => {
  const { onOpenSnackbar } = useSnackbarContext()
  const [differences, setDifferences] = useState(null)
  const [showSync, setShowSync] = useState(false)
  const [showDifferences, setShowDifferences] = useState(false)
  const { syncPatientData, isLoading } = useSyncPatientData(
    insuranceId,
    eligibilityId,
    existentPatientData.id
  )

  const handleSync = async () => {
    const res = await syncPatientData()
    if (!res.error) {
      onOpenSnackbar('Patient data synced successfully', 'success')
      setShowDifferences(false)
      setShowSync(false)
    } else {
      onOpenSnackbar('Failed to sync patient data', 'error')
    }
  }

  if (!patient)
    return (
      <Typography variant="span" fontWeight={200} textAlign="center">
        No Patient data found
      </Typography>
    )

  const getDifferences = (patient, existentPatientData) => {
    if (!patient || !existentPatientData) return null

    const differences = {}

    // Exclude keys that shouldn't be compared
    const excludedKeys = ['plan', 'relationship', 'gender']

    // Helper to normalize date formats
    const normalizeDate = date => {
      if (!date) return null // Treat null or empty as null

      try {
        // Handle string dates
        if (typeof date === 'string') {
          // Check if it contains slash or dash
          if (date.includes('/')) {
            const parts = date.split('/')
            if (parts.length === 3) {
              const [first, second, third] = parts
              // Format MM/DD/YYYY to YYYY-MM-DD
              return `${third}-${first.padStart(2, '0')}-${second.padStart(
                2,
                '0'
              )}`
            }
          } else if (date.includes('-')) {
            const parts = date.split('-')
            if (parts.length === 3) {
              const [first, second, third] = parts
              // Check if it's already in YYYY-MM-DD format
              if (first.length === 4) {
                // Assuming YYYY-DD-MM format, convert to YYYY-MM-DD
                return `${first}-${third.padStart(2, '0')}-${second.padStart(
                  2,
                  '0'
                )}`
              }
            }
          }
        }
        return date // Return as is if format is unknown
      } catch (error) {
        // If any error occurs, return the original value
        return date
      }
    }

    const normalizeValue = (value, key = null) => {
      if (value === null || value === undefined || value === '') return null // Treat empty and null as equivalent
      if (key === 'dob') return normalizeDate(value) // Normalize date fields
      if (typeof value === 'string') return value.trim() // Normalize strings
      return value // Return value as is for other types
    }

    const normalizeObject = obj => {
      const normalized = {}
      Object.keys(obj || {}).forEach(key => {
        normalized[key] = normalizeValue(obj[key], key)
      })
      return normalized
    }

    Object.keys(patient).forEach(key => {
      if (!excludedKeys.includes(key)) {
        if (key === 'address') {
          // Special handling for address fields
          const patientAddress = normalizeObject(patient[key])
          const existentAddress = normalizeObject(existentPatientData[key])

          const nestedDifferences = {}
          Object.keys(patientAddress).forEach(nestedKey => {
            const currentValue = patientAddress[nestedKey]
            const existingValue = normalizeValue(existentAddress[nestedKey])

            if (currentValue !== existingValue) {
              nestedDifferences[nestedKey] = {
                current: currentValue,
                existing: existingValue,
              }
            }
          })

          if (Object.keys(nestedDifferences).length > 0) {
            differences[key] = nestedDifferences
          }
        } else if (typeof patient[key] === 'object' && patient[key] !== null) {
          // Handle other nested objects
          const nestedDifferences = {}
          const nestedPatientData = patient[key]
          const nestedExistentPatientData = existentPatientData[key] || {}

          Object.keys(nestedPatientData).forEach(nestedKey => {
            const currentValue = normalizeValue(
              nestedPatientData[nestedKey],
              nestedKey
            )
            const existingValue = normalizeValue(
              nestedExistentPatientData[nestedKey],
              nestedKey
            )

            if (currentValue !== existingValue) {
              nestedDifferences[nestedKey] = {
                current: currentValue,
                existing: existingValue,
              }
            }
          })

          if (Object.keys(nestedDifferences).length > 0) {
            differences[key] = nestedDifferences
          }
        } else {
          // Compare scalar values
          const currentValue = normalizeValue(patient[key], key)
          const existingValue = normalizeValue(existentPatientData[key], key)

          if (currentValue !== existingValue) {
            differences[key] = {
              current: currentValue,
              existing: existingValue,
            }
          }
        }
      }
    })

    return differences
  }

  useEffect(() => {
    const diff = getDifferences(patient, existentPatientData?.insuranceForm)
    if (!diff) return
    if (Object.keys(diff).length > 0) {
      setDifferences(diff)
      setShowSync(true)
    } else {
      setShowSync(false)
    }
  }, [patient, existentPatientData])

  // Helper function to format date without timezone issues
  const formatDateDisplay = dateString => {
    if (!dateString) return 'N/A'

    try {
      // If it's already in MM/DD/YYYY format, return as is
      if (/^\d{1,2}\/\d{1,2}\/\d{4}$/.test(dateString)) return dateString

      let date

      // Try different date parsing approaches
      if (/^\d{4}-\d{2}-\d{2}/.test(dateString)) {
        // ISO format or similar (YYYY-MM-DD)
        // Extract just the date part if there's time info
        const datePart = dateString.split('T')[0]
        date = parseISO(datePart + 'T00:00:00')
      } else if (typeof dateString === 'string' && dateString.includes('-')) {
        // Handle YYYY-DD-MM format
        const parts = dateString.split('-')
        if (parts.length === 3 && parts[0].length === 4) {
          // Assume YYYY-MM-DD format
          date = new Date(parts[0], parseInt(parts[1]) - 1, parts[2])
        }
      } else {
        // Try parsing as a generic date
        date = new Date(dateString)
      }

      // Check if the date is valid
      if (!date || isNaN(date.getTime())) {
        // If parsing fails, try to return in a readable format
        if (typeof dateString === 'string') {
          return dateString
        }
        return 'N/A'
      }

      return format(date, 'MM/dd/yyyy')
    } catch (error) {
      console.warn('Error formatting date:', dateString, error)
      // If parsing fails completely, return the original string or N/A
      return dateString || 'N/A'
    }
  }

  return (
    <Wrapper>
      <Grid item xs={12} mb={2}>
        <Typography variant="h4" fontWeight={500}>
          Name:{' '}
          <Typography variant="span" fontWeight={200}>
            {patient?.firstName} {patient?.lastName}
          </Typography>
        </Typography>
      </Grid>
      {patient?.gender && (
        <Grid item xs={12} mb={2}>
          <Typography variant="h4" fontWeight={500}>
            Gender:{' '}
            <Typography variant="span" fontWeight={200}>
              {formatGender(patient?.gender || existentPatientData?.gender)}
            </Typography>
          </Typography>
        </Grid>
      )}
      {patient?.dob && (
        <Grid item xs={12} mb={2}>
          <Typography variant="h4" fontWeight={500}>
            Date of Birth:{' '}
            <Typography variant="span" fontWeight={200}>
              {formatDateDisplay(patient?.dob)}
            </Typography>
          </Typography>
        </Grid>
      )}
      <Grid item xs={12} mb={2}>
        <Typography variant="h4" fontWeight={500}>
          Relationship:{' '}
          <Typography variant="span" fontWeight={200}>
            {patient?.relationship}
          </Typography>
        </Typography>
      </Grid>
      <Divider margin="20px 0px" />
      <Grid item xs={12} mb={2}>
        <Typography variant="h4" fontWeight={500}>
          Patient Address Information:
        </Typography>
      </Grid>
      <Grid item xs={12} mb={2}>
        <Typography variant="h4" fontWeight={500}>
          City:{' '}
          <Typography variant="span" fontWeight={200}>
            {patient?.address?.city}
          </Typography>
        </Typography>
      </Grid>
      <Grid item xs={12} mb={2}>
        <Typography variant="h4" fontWeight={500}>
          State:{' '}
          <Typography variant="span" fontWeight={200}>
            {patient?.address?.state}
          </Typography>
        </Typography>
      </Grid>
      <Grid item xs={12} mb={2}>
        <Typography variant="h4" fontWeight={500}>
          Zipcode:{' '}
          <Typography variant="span" fontWeight={200}>
            {patient?.address?.zipcode}
          </Typography>
        </Typography>
      </Grid>
      <Grid item xs={12} mb={2}>
        <Typography variant="h4" fontWeight={500}>
          Address:{' '}
          <Typography variant="span" fontWeight={200}>
            {patient?.address?.address1}
          </Typography>
        </Typography>
      </Grid>
      <Divider margin="20px 0px" />
      {showDifferences && (
        <>
          <Grid item xs={12} mb={2}>
            <Typography variant="h4" fontWeight={500} mb="24px">
              The following data will be updated on the software:
            </Typography>
            {Object.keys(differences).map((key, index) => (
              <Grid item xs={12} mb={2} mt={2} key={index}>
                <Typography variant="h4" fontWeight={500}>
                  {key}:
                </Typography>
                {typeof differences[key].current !== 'undefined' ? (
                  <Grid container>
                    <Grid item xs={6}>
                      <Typography variant="span" fontSize={14} fontWeight={500}>
                        As It Is:{' '}
                        <Typography
                          variant="span"
                          fontSize={14}
                          fontWeight={200}
                        >
                          {differences[key].existing || 'N/A'}
                        </Typography>
                      </Typography>
                    </Grid>
                    <Grid item xs={6}>
                      <Typography variant="span" fontSize={14} fontWeight={500}>
                        To Be:{' '}
                        <Typography
                          variant="span"
                          fontSize={14}
                          fontWeight={200}
                        >
                          {differences[key].current || 'N/A'}
                        </Typography>
                      </Typography>
                    </Grid>
                  </Grid>
                ) : (
                  Object.keys(differences[key]).map(
                    (nestedKey, nestedIndex) => (
                      <Grid item xs={12} mb={2} mt={2} key={nestedIndex}>
                        <Typography
                          variant="h6"
                          fontWeight={400}
                          fontSize="13px"
                        >
                          {nestedKey}:
                        </Typography>
                        <Grid container>
                          <Grid item xs={4}>
                            <Typography
                              variant="span"
                              fontSize={14}
                              fontWeight={500}
                            >
                              As It Is:{' '}
                              <Typography
                                variant="span"
                                fontSize={14}
                                fontWeight={200}
                              >
                                {differences[key][nestedKey].existing || 'N/A'}
                              </Typography>
                            </Typography>
                          </Grid>
                          <Grid item xs={4}>
                            <Typography
                              variant="span"
                              fontSize={14}
                              fontWeight={500}
                            >
                              To Be:{' '}
                              <Typography
                                variant="span"
                                fontSize={14}
                                fontWeight={200}
                              >
                                {differences[key][nestedKey].current || 'N/A'}
                              </Typography>
                            </Typography>
                          </Grid>
                        </Grid>
                      </Grid>
                    )
                  )
                )}
              </Grid>
            ))}
          </Grid>

          <Grid
            display="flex"
            width={'100%'}
            justifyContent={'flex-end'}
            gap={1}
          >
            <Button
              variant="text"
              color="secondary"
              onClick={() => setShowDifferences(false)}
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              color="secondary"
              onClick={() => handleSync()}
              disabled={isLoading}
            >
              Confirm Patient Data Sync
            </Button>
          </Grid>
        </>
      )}
    </Wrapper>
  )
}

export default PatientData
